import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { useToast } from '../ToastContext';

const BLANK = {
  pickupLocation: '',
  deliveryLocation: '',
  incoterm: 'FCA',
  shipmentModePref: 'Air',
  requiredDeliveryTimeline: '',
  dimensionsNotes: '',
  hsnCodes: '',
  notes: '',
};

function toFormState(lr) {
  return {
    pickupLocation: lr.pickupLocation || '',
    deliveryLocation: lr.deliveryLocation || '',
    incoterm: lr.incoterm || 'FCA',
    shipmentModePref: lr.shipmentModePref || 'Air',
    requiredDeliveryTimeline: lr.requiredDeliveryTimeline
      ? lr.requiredDeliveryTimeline.slice(0, 10)
      : '',
    dimensionsNotes: lr.dimensionsNotes || '',
    hsnCodes: lr.hsnCodes || '',
    notes: lr.notes || '',
  };
}

export default function Logistics({ rfqId }) {
  const toast = useToast();
  const [form, setForm] = useState(BLANK);
  const [savedAt, setSavedAt] = useState(null);
  const [status, setStatus] = useState(null); // null = loading, false = not found, 'Draft'/'HandedOff'
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!rfqId) return;
    setStatus(null);
    api.getLogistics(rfqId)
      .then((lr) => {
        setForm(toFormState(lr));
        setSavedAt(lr.updatedAt);
        setStatus(lr.status);
      })
      .catch((err) => {
        if (err.status === 404) {
          setStatus(false);
          setForm(BLANK);
          setSavedAt(null);
        } else {
          toast.push(err.message, { variant: 'error' });
        }
      });
    // eslint-disable-next-line
  }, [rfqId]);

  function setField(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function save() {
    setBusy(true);
    try {
      const payload = {
        ...form,
        requiredDeliveryTimeline: form.requiredDeliveryTimeline || undefined,
      };
      const lr = await api.patchLogistics(rfqId, payload);
      setForm(toFormState(lr));
      setSavedAt(lr.updatedAt);
      setStatus(lr.status);
      toast.push('Logistics draft saved');
    } catch (err) {
      toast.push(err.message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h2 className="card-title">Linked logistics requirement</h2>

      <div className="banner review" style={{ marginBottom: 16 }}>
        <span>⚠</span>
        <div>Logistics orchestration is a Phase 2 capability. Phase 1 captures incoterm, pickup and delivery locations only.</div>
      </div>

      {/* Status banner */}
      <div className="banner" style={{ marginBottom: 16, background: 'var(--surface-1)', border: '1px solid var(--border-1)' }}>
        <span style={{ fontWeight: 600 }}>Status: {status === null ? '…' : status === false ? 'None' : status}</span>
        {savedAt && (
          <span style={{ marginLeft: 16, fontSize: 12, color: 'var(--ink-500)' }}>
            Last saved: {new Date(savedAt).toLocaleString()}
          </span>
        )}
        {status === false && (
          <span style={{ marginLeft: 16, fontSize: 12, color: 'var(--ink-500)' }}>
            No logistics requirement linked yet — create one when committing the decision, or save a draft below.
          </span>
        )}
      </div>

      <div className="card">
        <div className="row">
          <label className="field">
            <span className="label">Pickup location</span>
            <input
              className="input"
              value={form.pickupLocation}
              onChange={(e) => setField('pickupLocation', e.target.value)}
              placeholder="e.g. SKF Gothenburg warehouse"
            />
          </label>
          <label className="field">
            <span className="label">Delivery location</span>
            <input
              className="input"
              value={form.deliveryLocation}
              onChange={(e) => setField('deliveryLocation', e.target.value)}
              placeholder="e.g. Boeing Seattle MRO dock 4"
            />
          </label>
        </div>

        <div className="row">
          <label className="field">
            <span className="label">Incoterm</span>
            <select
              className="select"
              value={form.incoterm}
              onChange={(e) => setField('incoterm', e.target.value)}
            >
              <option value="FCA">FCA</option>
              <option value="EXW">EXW</option>
              <option value="CIP">CIP</option>
              <option value="DAP">DAP</option>
              <option value="DDP">DDP</option>
            </select>
          </label>
          <label className="field">
            <span className="label">Shipment mode</span>
            <select
              className="select"
              value={form.shipmentModePref}
              onChange={(e) => setField('shipmentModePref', e.target.value)}
            >
              <option value="Air">Air</option>
              <option value="Sea">Sea</option>
              <option value="Road">Road</option>
            </select>
          </label>
          <label className="field">
            <span className="label">Required delivery</span>
            <input
              className="input"
              type="date"
              value={form.requiredDeliveryTimeline}
              onChange={(e) => setField('requiredDeliveryTimeline', e.target.value)}
            />
          </label>
        </div>

        <label className="field">
          <span className="label">HSN codes (comma-separated)</span>
          <input
            className="input"
            value={form.hsnCodes}
            onChange={(e) => setField('hsnCodes', e.target.value)}
            placeholder="e.g. 8482.10, 8483.20"
          />
        </label>

        <label className="field">
          <span className="label">Dimensions / packaging notes</span>
          <textarea
            className="input"
            rows={2}
            value={form.dimensionsNotes}
            onChange={(e) => setField('dimensionsNotes', e.target.value)}
            placeholder="e.g. 2 crates, each 60x40x30cm, 25kg"
            style={{ resize: 'vertical' }}
          />
        </label>

        <label className="field">
          <span className="label">Notes</span>
          <textarea
            className="input"
            rows={3}
            value={form.notes}
            onChange={(e) => setField('notes', e.target.value)}
            placeholder="Additional logistics notes…"
            style={{ resize: 'vertical' }}
          />
        </label>

        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button className="btn btn-secondary" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save draft'}
          </button>
          <button
            className="btn"
            disabled
            title="Phase 2 — logistics handoff goes to a separate module"
          >
            Hand off to logistics
          </button>
        </div>
      </div>
    </div>
  );
}
